import React, { memo } from 'react';
import { EdgeProps, getSmoothStepPath, EdgeLabelRenderer } from 'reactflow';

interface RelationshipEdgeData {
  relationship: string;
  strength?: 'weak' | 'medium' | 'strong';
  label?: string;
}

const RelationshipEdge: React.FC<EdgeProps<RelationshipEdgeData>> = ({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  style = {},
  markerEnd,
}) => {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Determine edge style based on relationship type and strength
  const getEdgeStyle = () => {
    const relationship = data?.relationship || 'default';
    const strength = data?.strength || 'medium';
    
    // Base styles
    const baseStyle = {
      ...style,
      strokeWidth: 2,
    };
    
    // Strength modifiers
    const strengthModifiers = {
      weak: { strokeWidth: 1, opacity: 0.7 },
      medium: { strokeWidth: 2, opacity: 1 },
      strong: { strokeWidth: 3, opacity: 1 },
    };
    
    // Relationship colors
    const relationshipColors: Record<string, string> = {
      default: '#9ca3af', // gray
      parent: '#8b5cf6', // purple
      child: '#8b5cf6', // purple
      sibling: '#10b981', // green
      reference: '#f59e0b', // amber
      dependency: '#ef4444', // red
      collaboration: '#3b82f6', // blue
    };
    
    // Find the closest matching relationship type
    const relationshipType = Object.keys(relationshipColors).find(
      type => relationship.toLowerCase().includes(type.toLowerCase())
    ) || 'default';
    
    return {
      ...baseStyle,
      ...strengthModifiers[strength],
      stroke: relationshipColors[relationshipType],
    };
  };

  return (
    <>
      <path
        id={id}
        style={getEdgeStyle()}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
      />
      
      {(data?.label || data?.relationship) && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              background: 'white',
              padding: '2px 4px',
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 500,
              pointerEvents: 'all',
              border: '1px solid #e2e8f0',
            }}
            className="nodrag nopan"
          >
            {data.label || data.relationship}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default memo(RelationshipEdge);
